import { isAxiosError } from 'axios';
import {
  catchError,
  concat,
  EMPTY,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  timer,
} from 'rxjs';
import {
  createSpanName,
  SpanComponent,
  instrument,
} from '@imprivata-cloud/common';
import type { OrgPreferences } from '@imprivata-cloud/authn';
import { client } from './client';
import { PRE_LOGIN_TIMEOUT_MS } from './constants';
import getConfigs from '../appConfigUtils';

const { API_URL } = getConfigs();

export const getOrgPreferences = instrument(
  createSpanName(SpanComponent.UI, 'service', 'getOrgPreferences'),
  async (signal?: AbortSignal): Promise<OrgPreferences> => {
    return client.post<OrgPreferences, OrgPreferences>(
      `${API_URL}/authn-web/org-preferences/v2/org-preferences/get`,
      undefined,
      { timeout: PRE_LOGIN_TIMEOUT_MS, signal },
    );
  },
);

// at boot the page can load before the endpoint's network is up: a request that got no
// response may succeed later, while a server answer won't change
export const gotNoResponse = (error: unknown): boolean =>
  isAxiosError(error) && !error.response;

const FIRST_RETRY_DELAY_MS = 1000;
const LATER_RETRIES = 4;

// unsubscribing cancels the in-flight request
const requestOrgPreferences$ = new Observable<OrgPreferences>(subscriber => {
  const controller = new AbortController();
  getOrgPreferences(controller.signal).then(
    preferences => {
      subscriber.next(preferences);
      subscriber.complete();
    },
    (error: unknown) => {
      subscriber.error(error);
    },
  );
  return () => {
    controller.abort();
  };
});

const retryWhileUnreachable$ = timer(FIRST_RETRY_DELAY_MS).pipe(
  switchMap(() =>
    requestOrgPreferences$.pipe(
      retry({
        count: LATER_RETRIES,
        delay: (error: unknown, retryCount) =>
          gotNoResponse(error)
            ? timer(FIRST_RETRY_DELAY_MS * 2 ** retryCount)
            : throwError(() => error),
      }),
    ),
  ),
  catchError(() => EMPTY),
);

// first the first attempt's answer (null on failure), then, if it got no response,
// the preferences recovered by retrying at 1, 2, 4, 8 and 16 s
export const orgPreferences$: Observable<OrgPreferences | null> =
  requestOrgPreferences$.pipe(
    catchError((error: unknown) =>
      gotNoResponse(error)
        ? concat(of(null), retryWhileUnreachable$)
        : of(null),
    ),
  );
